import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import espnowService from '../services/espnowIrrigation';

/**
 * Whether the signed-in person is a FARM VIEWER in Irrigation (Rob,
 * 2026-10-28): an irrigation admin has given them only some farms on
 * /irrigation/people. A viewer sees those farms and may Stop watering, and
 * nothing else; the server refuses every other change (403 VIEWER_ONLY), so
 * this only decides what the pages offer.
 *
 * One request per person and organisation, shared by every component that
 * asks. A failed request reads as not a viewer: the server still refuses.
 */

type Scope = number[] | null;
const cache = new Map<string, Promise<Scope>>();

export function useIrrigationViewer(): { viewer: boolean; farmIds: Scope; loading: boolean } {
  const { user } = useAuth();
  const key = `${user?.id ?? ''}:${user?.organisation_id ?? ''}`;
  const [result, setResult] = useState<{ key: string; farmIds: Scope } | null>(null);

  useEffect(() => {
    if (!user) return;
    let alive = true;
    let p = cache.get(key);
    if (!p) {
      p = espnowService.getMyFarms().catch(() => null);
      cache.set(key, p);
    }
    p.then((farmIds) => { if (alive) setResult({ key, farmIds }); });
    return () => { alive = false; };
  }, [key, user]);

  const current = result?.key === key ? result : null;
  return { viewer: current?.farmIds != null, farmIds: current?.farmIds ?? null, loading: !current };
}

/** Forget the cached answer, after an admin changes someone's farms. */
export function clearIrrigationViewerCache(): void {
  cache.clear();
}
